import {
  ArrowPathRoundedSquareIcon,
  ArrowUpRightIcon,
  ChatBubbleLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { format } from "date-fns";
import { Duration } from "luxon";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Button, Headline, Label, Paragraph, Tooltip } from "@fronterahealth/frontera-ui-components";
import { VideoPlayerHandle } from "@fronterahealth/frontera-ui-components";

import { ThumbnailManifestType, useGetMediaThumbnailManifestQuery } from "@api/graphql/types-and-hooks";
import { MediaTargetPredictionFeedbackDialog } from "@components/MediaTargetPredictionFeedbackDialog/MediaTargetPredictionFeedbackDialog";
import { SessionGroup } from "@pages/ClientProgramming/ProgrammingGoalVideoMoments/ProgrammingGoalVideoMoments";
import { VideoMomentPlayer } from "@pages/ClientProgramming/ProgrammingGoalVideoMoments/VideoMomentPlayer";
import { EmojiCommentButtonsv2 } from "@pages/VideoSessionsV2/components/EmojiComments/EmojiCommentButtonsv2";
import { EmojiCommentCard } from "@pages/VideoSessionsV2/components/EmojiComments/EmojiCommentCard";
import { useEmojiMarkers } from "@pages/VideoSessionsV2/hooks/useEmojiMarkers";
import { useClientProgrammingData } from "@providers/ClientProgrammingProvider";

interface PlayAllVideoMomentsDialogProps {
  groupedVideoSessions: [string, SessionGroup][];
  onClose: () => void;
}

interface VideoClip {
  id: string;
  mediaId: string;
  startTime: number;
  endTime: number;
  rationale: string;
  targetName: string;
  goalName: string;
  mediaUrl: string;
  thumbnailManifest?: ThumbnailManifestType | null;
  sessionGroup: SessionGroup;
}

// Helper function to convert VideoClip to MediaTargetPredictionType
const convertVideoClipToMediaTargetPrediction = (videoClip: VideoClip) => {
  // Find the original session data
  const originalSession = videoClip.sessionGroup.sessions.find((s) => s.id === videoClip.id);

  if (!originalSession) {
    throw new Error(`Could not find original session data for video clip ${videoClip.id}`);
  }

  return {
    ...originalSession,
  };
};

export const PlayAllVideoMomentsPlaylist: React.FC<PlayAllVideoMomentsDialogProps> = ({
  groupedVideoSessions,
  onClose,
}) => {
  const { currentlyPlayingClipID, setCurrentlyPlayingClipID } = useClientProgrammingData();

  const [openFeedback, setOpenFeedback] = useState<boolean>(false);
  const [isLooping, setIsLooping] = useState<boolean>(false);
  const videoPlayerRef = useRef<VideoPlayerHandle>(null);
  const [currentDuration, setCurrentDuration] = useState<number>(0);

  // Transform groupedVideoSessions into a flat array of video clips
  const videoClips: VideoClip[] = useMemo(() => {
    const clips: VideoClip[] = [];

    groupedVideoSessions.forEach(([mediaId, sessionGroup]) => {
      if (!sessionGroup?.media?.signedUploadUrl) return;

      sessionGroup.sessions.forEach((session) => {
        clips.push({
          ...session,
          mediaId,
          rationale: session.rationale || "",
          targetName: session.target?.name || "",
          goalName: session.goal?.name || "",
          mediaUrl: sessionGroup.media?.signedUploadUrl || "",
          sessionGroup,
        });
      });
    });

    return clips;
  }, [groupedVideoSessions]);

  const [currentClipIndex, setCurrentClipIndex] = useState<number | null>(
    currentlyPlayingClipID ? videoClips.findIndex((clip) => clip.id === currentlyPlayingClipID) : 0,
  );
  const currentClip = currentClipIndex !== null && videoClips.length > 0 ? videoClips[currentClipIndex] : null;
  const hasNextClip = currentClipIndex !== null && currentClipIndex < videoClips.length - 1;
  const hasPreviousClip = currentClipIndex !== null && currentClipIndex > 0;

  const { data, isLoading } = useGetMediaThumbnailManifestQuery(
    {
      mediaId: currentClip?.mediaId || "",
    },
    {
      queryKey: ["getMediaManifest", currentClip],
    },
  );

  const thumbnailManifest = data?.getMediaById?.edges[0]?.node?.thumbnailManifest;

  const { handleEmojiClick, selectedEmoji, handleCommentSubmit, closeCommentBox, isCommentSaving } = useEmojiMarkers({
    mediaId: currentClip?.mediaId || "",
    videoDuration: currentDuration,
  });
  useEffect(() => {
    if (currentClipIndex !== null && currentClipIndex >= 0 && currentClipIndex < videoClips.length) {
      setCurrentlyPlayingClipID(videoClips[currentClipIndex].id);
      videoPlayerRef.current?.playInRange(videoClips[currentClipIndex].startTime, videoClips[currentClipIndex].endTime);
    } else {
      setCurrentlyPlayingClipID(null);
    }
  }, [currentClipIndex]);

  const handleStopLooping = useCallback(() => {
    if (videoPlayerRef.current?.isLooping) {
      setIsLooping(false);
      videoPlayerRef.current.stopLooping();
    }
  }, [videoPlayerRef.current?.isLooping]);

  const handleNextClip = useCallback(() => {
    if (hasNextClip) {
      setCurrentClipIndex(currentClipIndex + 1);
      handleStopLooping();
    }
  }, [currentClipIndex, hasNextClip]);

  const handlePreviousClip = useCallback(() => {
    if (hasPreviousClip) {
      setCurrentClipIndex(currentClipIndex - 1);
      handleStopLooping();
    }
  }, [currentClipIndex, hasPreviousClip]);

  const handleLoopClick = useCallback(() => {
    if (currentClip) {
      if (isLooping) {
        videoPlayerRef.current?.stopLooping();
        setIsLooping(false);
      } else {
        videoPlayerRef.current?.loopInRange(currentClip.startTime, currentClip.endTime);
        setIsLooping(true);
      }
    }
  }, [currentClip, isLooping]);

  if (videoClips.length === 0) {
    return (
      <div className="p-6 text-center">
        <Headline size="small">No video clips available</Headline>
        <Paragraph colorType="secondary" className="mt-2">
          There are no video moments to play.
        </Paragraph>
        <Button appearance="secondary" text="Close" onClick={onClose} className="mt-4" />
      </div>
    );
  }
  const getCurrentTime = () => {
    return videoPlayerRef.current?.player?.currentTime() || 0;
  };

  const handlePlayPause = (pause: boolean) => {
    const player = videoPlayerRef.current?.player;
    if (player) pause ? player.pause() : player.play();
  };

  const handleClose = () => {
    closeCommentBox();
    handlePlayPause(false);
  };

  const handleSubmit = (comment: string, timestamp: number) => {
    handleCommentSubmit(comment, timestamp);
    handlePlayPause(false);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pb-2 -mx-4 border-b border-limestone-200">
        <div className="flex items-center gap-2">
          <Paragraph size="medium" colorType="primary" className="whitespace-pre-wrap">
            {currentClip?.sessionGroup?.media?.createdDate
              ? format(currentClip.sessionGroup.media.createdDate, "M-d-yyyy @ h:mmaa")
              : ""}
          </Paragraph>
          <Link
            to={`/video-sessions-v2/${currentClip?.mediaId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex text-limestone-600 hover:text-limestone-800 hover:translate-x-0.5 hover:-translate-y-0.5 transition-all"
          >
            <ArrowUpRightIcon className="h-5 w-5" />
          </Link>
        </div>
      </div>

      {/* Video Player */}
      <div className="flex-1 p-2 py-2">
        {currentClip && !isLoading && (
          <div className="space-y-2">
            {/* Current Clip Info */}
            <div className="bg-limestone-50 p-3 rounded-lg">
              <div className="flex justify-between items-start">
                <div className="flex flex-row gap-4 items-center">
                  <Paragraph colorType="secondary" size="small" className="mt-1 text-interaction-primary">
                    {Duration.fromObject({ seconds: currentClip.startTime }).toFormat("hh:mm:ss")} -{" "}
                    {Duration.fromObject({ seconds: currentClip.endTime }).toFormat("hh:mm:ss")}
                  </Paragraph>
                  <div
                    onClick={() => {
                      handleLoopClick();
                    }}
                    className={clsx(
                      "p-1 rounded-md border-limestone-200 border bg-white w-6 h-6 flex items-center justify-center cursor-pointer hover:bg-limestone-50 transition-colors",
                      isLooping ? "bg-green-700! border-green-700! text-white!" : "",
                    )}
                    tabIndex={0}
                    role="button"
                  >
                    <ArrowPathRoundedSquareIcon
                      className={clsx("h-4 w-4", isLooping ? "text-white" : "text-limestone-600")}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Label size="small">
                    {currentClipIndex !== null ? currentClipIndex + 1 : "-"} of {videoClips.length}
                  </Label>
                  <Button
                    appearance="secondary"
                    text={
                      <div className="flex items-center gap-1">
                        <ChevronLeftIcon className="h-4 w-4 text-limestone-800" />
                        <Label size="small">Previous clip</Label>
                      </div>
                    }
                    onClick={handlePreviousClip}
                    disabled={!hasPreviousClip}
                    className="p-1! rounded-[4px]! bg-white"
                  />
                  <Button
                    text={
                      <div className="flex items-center gap-1">
                        <ChevronRightIcon className="h-4 w-4 text-limestone-800" />
                        <Label size="small">Next clip</Label>
                      </div>
                    }
                    appearance="secondary"
                    onClick={handleNextClip}
                    disabled={!hasNextClip}
                    className="p-1! rounded-[4px]! bg-white"
                  />
                </div>
              </div>
            </div>

            {/* Video Player */}
            <div className="relative">
              <VideoMomentPlayer
                ref={videoPlayerRef}
                videoUrl={currentClip.mediaUrl}
                startTime={currentClip.startTime}
                endTime={currentClip.endTime}
                thumbnailManifest={thumbnailManifest}
                onPause={handleStopLooping}
                onDurationChange={setCurrentDuration}
              />
            </div>
            <div className="flex flex-row justify-between items-center">
              <EmojiCommentButtonsv2
                onEmojiClick={handleEmojiClick}
                disabled={currentDuration <= 0}
                playerRef={videoPlayerRef}
                mediaId={currentClip.mediaId}
                className="justify-start! lg:ml-0"
              />
              <Tooltip text="Leave Feedback" showInfoIcon={false}>
                <ChatBubbleLeftIcon
                  className="h-5 w-5 text-limestone-800 cursor-pointer"
                  onClick={() => setOpenFeedback(true)}
                />
              </Tooltip>
            </div>

            {selectedEmoji && (
              <EmojiCommentCard
                selectedEmoji={selectedEmoji}
                currentTime={getCurrentTime()}
                onSubmit={handleSubmit}
                onClose={handleClose}
                isCommentSaving={isCommentSaving}
                containerClassName="border-b-0!"
              />
            )}
            {currentClip && (
              <MediaTargetPredictionFeedbackDialog
                open={openFeedback}
                setOpen={setOpenFeedback}
                mediaTargetPrediction={convertVideoClipToMediaTargetPrediction(currentClip)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
